/**
 * ListFilterBar — canonical search + filter toolbar for backup-management lists.
 *
 * Thin wrapper over the kit <FilterBar>: a search box (left), optional filter
 * controls (children — kit <Select>s), and a live result count (right). Keeps
 * every backup list's search/filter UX identical and design-blueprint-conformant.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */
import React from 'react';
import { Search } from 'lucide-react';
import { FilterBar, Input } from '@/components/ui';

export interface ListFilterBarProps {
  query: string;
  onQuery: (v: string) => void;
  placeholder?: string;
  count: number;
  total: number;
  /** Extra filter controls (kit <Select>s) rendered next to the search box. */
  children?: React.ReactNode;
}

export const ListFilterBar: React.FC<ListFilterBarProps> = ({
  query,
  onQuery,
  placeholder,
  count,
  total,
  children,
}) => (
  <FilterBar
    trailing={
      <span className="whitespace-nowrap text-sm text-[var(--color-text-secondary)]">
        {count === total ? `${total}` : `${count} of ${total}`} {total === 1 ? 'item' : 'items'}
      </span>
    }
  >
    <div className="flex items-center gap-2">
      <Search className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
      <Input
        className="w-56"
        placeholder={placeholder ?? 'Search…'}
        value={query}
        onChange={(e) => onQuery(e.target.value)}
      />
    </div>
    {children}
  </FilterBar>
);

/** Case-insensitive substring match across the provided fields. Empty query = match all. */
export function textMatch(query: string, ...fields: Array<string | number | null | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => String(f ?? '').toLowerCase().includes(q));
}

export default ListFilterBar;
